import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CompetitionRibbon from "../components/CompetitionRibbon";
import YouTubeEmbed from "../components/YouTubeEmbed";
import usePageMeta from "../lib/usePageMeta";
import { onboardingVideo, trainingCategories } from "../data/trainingVideos";
import { AlertTriangle, ArrowRight, ShieldCheck, Globe, Rocket, ExternalLink } from "lucide-react";

const LEAD_MOTIVH_URL = "https://www.leadmotivhhayti.net";

const CATEGORY_ICONS = {
  shield: ShieldCheck,
  globe: Globe,
  rocket: Rocket,
};

export default function Landing() {
  const { t, lang } = useI18n();
  usePageMeta({
    title: t("hub.metaTitle") + " · Seayisyen",
    description: t("hub.metaDesc"),
    path: "/",
  });

  // Build localized category metadata once per language change.
  const localizedCategories = useMemo(
    () => trainingCategories.map((c) => ({
      ...c,
      label: t(`hub.categories.${c.key}.label`),
      desc: t(`hub.categories.${c.key}.desc`),
    })),
    [lang, t]
  );

  return (
    <div data-testid="landing-page" key={lang}>
      <CompetitionRibbon />
      <Header />

      {/* ─────────────────────────────────────────────────────────────
          ACTION BANNER (top) — required LeadMotivH onboarding
         ───────────────────────────────────────────────────────────── */}
      <section
        className="relative border-b border-[#1a1a1a] bg-gradient-to-b from-[#1a0303] via-black to-black"
        data-testid="hub-action-banner"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-16 grid lg:grid-cols-5 gap-10 items-center">
          {/* Banner copy + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="lg:col-span-3"
            data-testid="hub-banner-copy"
          >
            <div className="inline-flex items-center gap-2 border border-[#e6192b]/60 bg-[#e6192b]/10 px-3 py-1.5 mb-5">
              <AlertTriangle size={14} className="text-[#e6192b]" />
              <span className="overline text-[#e6192b]">{t("hub.banner.tag")}</span>
            </div>
            <h1
              className="font-serif text-3xl md:text-5xl leading-[1.08] tracking-tight"
              data-testid="hub-banner-headline"
            >
              {t("hub.banner.headline")}
            </h1>
            <p className="mt-5 text-neutral-300 text-base md:text-lg leading-relaxed max-w-2xl">
              {t("hub.banner.body")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={LEAD_MOTIVH_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hub-leadmotivh-btn"
                className="btn-imperial inline-flex items-center gap-2"
              >
                {t("hub.banner.cta")} <ExternalLink size={14} />
              </a>
              <Link
                to="/cohort-info"
                data-testid="hub-banner-info-btn"
                className="btn-outline-gold inline-flex items-center gap-2"
              >
                {t("hub.banner.infoCta")} <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>

          {/* Onboarding video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.65, delay: 0.1 }}
            className="lg:col-span-2"
            data-testid="hub-onboarding-video-wrap"
          >
            <div className="overline mb-3 text-[#d4af37]">{t("hub.banner.videoOverline")}</div>
            <YouTubeEmbed
              url={`https://youtu.be/${onboardingVideo.youtubeId}`}
              title="LeadMotivH · onboarding"
              testId="hub-onboarding-video"
            />
            <div className="mt-3 text-xs text-neutral-500 leading-relaxed">
              {t("hub.banner.videoCaption")}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          VIDEO TRAINING HUB
         ───────────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28" data-testid="hub-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-14">
            <div className="overline mb-4">{t("hub.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="hub-title">
              {t("hub.title")}
            </h2>
            <p className="mt-4 text-neutral-400 leading-relaxed">{t("hub.subtitle")}</p>
          </div>

          <div className="space-y-20">
            {localizedCategories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.icon] || Rocket;
              return (
                <div key={cat.key} data-testid={`hub-category-${cat.key}`}>
                  {/* Category heading */}
                  <div className="flex items-start gap-4 mb-8">
                    <div className="w-12 h-12 border border-[#d4af37]/40 bg-[#d4af37]/5 flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-[#d4af37]" />
                    </div>
                    <div>
                      <div className="overline text-[#d4af37]">{t("hub.categoryOverline")}</div>
                      <h3 className="font-serif text-2xl md:text-3xl mt-1" data-testid={`hub-category-label-${cat.key}`}>
                        {cat.label}
                      </h3>
                      {cat.desc && (
                        <p className="mt-1 text-sm text-neutral-500 max-w-xl">{cat.desc}</p>
                      )}
                    </div>
                  </div>

                  {/* Video grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`hub-grid-${cat.key}`}>
                    {cat.videos.map((v, idx) => (
                      <VideoCard key={v.id} video={v} index={idx} catKey={cat.key} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* NOTE: The "Ready to enroll? Pick your cohort..." footer block was
              intentionally removed because registration is closed for the current
              session. The /enroll and /enroll-advanced routes + their page
              components are preserved in the repo (App.js + pages/Enroll.jsx +
              pages/EnrollAdvanced.jsx) so we can re-link them quickly when the
              next cohort opens. */}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function VideoCard({ video, index, catKey }) {
  const { t } = useI18n();
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.2) }}
      className="card-imperial p-4 md:p-5 flex flex-col"
      data-testid={`hub-card-${catKey}-${video.id}`}
    >
      <YouTubeEmbed
        url={`https://youtu.be/${video.youtubeId}`}
        title={video.title}
        testId={`hub-video-${video.id}`}
      />
      <h4
        className="mt-4 font-serif text-base md:text-lg leading-snug text-white"
        data-testid={`hub-card-title-${video.id}`}
      >
        {video.title}
      </h4>
      {video.meta && video.meta.type === "affiliate" && (
        <a
          href={video.meta.href}
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-testid={`hub-card-affiliate-${video.id}`}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-condensed tracking-widest uppercase text-[#d4af37] hover:text-white transition-colors"
        >
          ✦ {video.meta.label || t("hub.affiliateLink")} <ExternalLink size={11} />
        </a>
      )}
      <a
        href={`https://youtu.be/${video.youtubeId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-condensed tracking-widest uppercase text-neutral-500 hover:text-[#d4af37] transition-colors w-fit"
        data-testid={`hub-card-yt-${video.id}`}
      >
        {t("hub.watchOnYouTube")} <ExternalLink size={10} />
      </a>
    </motion.article>
  );
}
